import { Suspense, useState, useEffect, useLayoutEffect, useRef } from "react"
import { createPortal } from "react-dom"
import PropTypes from "prop-types"
import classnames from "classnames"
import Mustache from "mustache"

import { buildFilterContextDataForDIY } from "shared/components/filter_code/build_filter_context_data"
import ErrorMessageForField from "./error_message"
import { generateFilterCodeWithAI, loadFilterCodeGenerators, validateFilterCode } from "./api"

import generateApiList from "./filter_code/api_list"
import FilterCodeIngredientListbox from "./filter_code/ingredient_listbox"
import OptionsPanel from "./filter_code/options_panel"
import GeneratorModal from "./filter_code/generator_modal"
import AIGeneratorModal from "./filter_code/ai_generator_modal"

import trim from "lodash/trim"

import styles from "./filter_code.module.scss"

import MonacoEditor from "shared/components/monaco_editor/index.jsx"
import { verifyIntendedPromiseRejection } from "./utils/modal_promise"

export default function FilterCode({
  contextTemplate,
  selectedTrigger,
  selectedQueries,
  selectedActions,
  filterCode,
  updateFilterCode,
  proFeatureGate,
  userPermissions,
  ctaSlot,
}) {
  const contextData = {
    ...buildFilterContextDataForDIY({
      selectedTrigger,
      selectedQueries,
      selectedActions,
    }),
    hasAppletDataStorePermission: userPermissions?.applet_data_store?.permitted,
  }
  const apiList = generateApiList({ ...contextData, userPermissions })

  const filterCodePlaceholder = `// Add your code here. All actions will run unless you explicitly skip them.
// Quick tips!
// Auto-complete is on. Start typing to see ingredient options.
// Hover over any ingredient to see the data type and an example.
// F1 to list keyboard shortcuts.
// TypeScript 5.9 (ES2024)
`
  const [code, updateCode] = useState(filterCode || filterCodePlaceholder)
  const [prompt, setPrompt] = useState("")
  const [generators, updateGenerators] = useState([])
  const [selectedGenerator, updateSelectedGenerator] = useState(null)
  const [showAIGenerator, updateShowAIGenerator] = useState(false)
  const [generatedCode, setGeneratedCode] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [aiError, setAiError] = useState("")
  const [rateLimited, setRateLimited] = useState(false)
  const [errors, updateErrors] = useState([])
  const [validFilterCode, updateValidFilterCode] = useState(false)
  const [submitTriggered, updateSubmitTriggered] = useState(false)
  const rootRef = useRef(null)
  const [offsetTop, setOffsetTop] = useState(0)

  // The editor fills the viewport below whatever header hosts this screen; the
  // header can grow without a window resize (banner, errors), so watch the host.
  useLayoutEffect(() => {
    const root = rootRef.current
    const measure = () => {
      if (rootRef.current) setOffsetTop(Math.round(rootRef.current.getBoundingClientRect().top + window.scrollY))
    }

    measure()
    window.addEventListener("resize", measure)
    const observer = new ResizeObserver(measure)
    if (root?.parentElement) observer.observe(root.parentElement)

    return () => {
      window.removeEventListener("resize", measure)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    loadFilterCodeGenerators(
      [selectedTrigger.id],
      selectedQueries.map(q => q["id"]),
      selectedActions.map(a => a["id"])
    ).then(result => {
      updateGenerators(result)
    })
  }, [])

  useEffect(() => {
    const trimmedCode = trim(code)
    if (trimmedCode === trim(filterCodePlaceholder)) {
      updateValidFilterCode(false)
    } else {
      updateValidFilterCode(true)
    }
  }, [code])

  const buttonText = () => {
    let text = "Add filter code"

    if (filterCode === "") {
      text = submitTriggered ? "Adding filter code..." : "Add filter code"
    } else {
      text = submitTriggered ? "Updating filter code..." : "Update filter code"
    }

    return text
  }

  const onClick = () => {
    updateSubmitTriggered(true)

    if (trim(code) === "") {
      updateFilterCode(null)
      return
    }

    validateFilterCode({
      filter_code: code,
      trigger: selectedTrigger,
      queries: selectedQueries,
      actions: selectedActions,
    })
      .then(result => {
        // A missing payload is a failed validation; a null errors list is a clean one.
        if (!result) throw new Error("missing validation payload")
        const errors = result.errors ?? []

        if (errors.length) {
          updateErrors(errors)
          updateSubmitTriggered(false)
        } else {
          updateFilterCode(code)
        }
      })
      .catch(() => {
        updateErrors([{ message: "Unable to validate filter code" }])
        updateSubmitTriggered(false)
      })
  }

  const askAItoGenerateFilterCode = () => {
    setIsGenerating(true)
    setAiError("")
    generateFilterCodeWithAI({
      user_description: prompt,
      trigger: selectedTrigger,
      queries: selectedQueries,
      actions: selectedActions,
      existing_code: code,
    })
      .then(result => {
        if (result.value) {
          setGeneratedCode(result.value)
        } else {
          if (result.errors.find(error => error.attribute === "rate_limit")) {
            setRateLimited(true)
            setAiError("You have reached your limit of AI code generation attempts. Please try again later.")
          } else {
            setAiError("Sorry, we could not generate code from your prompt. Please try again.")
          }
        }
      })
      .catch(error => {
        console.log(error)
        setAiError("Sorry, we could not generate code from your prompt. Please try again.")
      })
      .finally(() => {
        setIsGenerating(false)
      })
  }

  const cta = (
    <button
      type="button"
      className={classnames("button-tertiary", styles.cta)}
      disabled={!validFilterCode || submitTriggered}
      onClick={onClick}
    >
      {buttonText()}
    </button>
  )

  return (
    <div ref={rootRef} className={styles["filter-code"]} style={{ "--filter-code-top": `${offsetTop}px` }} data-filter-code>
      {ctaSlot && createPortal(cta, ctaSlot)}

      <section className={styles.editor}>
        {errors.length > 0 && (
          <div className={styles.errors}>
            {errors.map(e => (
              <ErrorMessageForField key={e.message} errors={e} field="message" variant="on-black" />
            ))}
          </div>
        )}
        <Suspense fallback="Loading...">
          <MonacoEditor
            positionAbsolute={false}
            containerClassName={styles.code}
            height="auto"
            theme="ifttt-dark"
            editorOptions={{
              lineHeight: 24,
              lineNumbersMinChars: 2,
              lineDecorationsWidth: 8,
              suggestLineHeight: 22,
              scrollbar: { verticalScrollbarSize: 16 },
            }}
            defaultValue={code}
            onChange={updateCode}
            typescriptContext={Mustache.render(contextTemplate, contextData)}
          />
        </Suspense>
      </section>

      <OptionsPanel>
        <FilterCodeIngredientListbox
          apiList={apiList["Triggers"]}
          name="Triggers"
          serviceList={[selectedTrigger.service]}
        />

        <FilterCodeIngredientListbox
          apiList={apiList["Queries"]}
          name="Queries"
          serviceList={selectedQueries.map(q => q.service)}
        />

        <FilterCodeIngredientListbox
          apiList={apiList["Actions"]}
          name="Actions"
          serviceList={selectedActions.map(q => q.service)}
        />

        <FilterCodeIngredientListbox
          apiList={apiList["More"]}
          name="More"
        />

        {userPermissions?.applet_data_store?.permitted && (
          <FilterCodeIngredientListbox
            apiList={apiList["DataStore"]}
            name="DataStore"
          />
        )}

        <section className={styles.generators}>
          <h4>Code generators</h4>
          <ul>
            <li>
              <button
                type="button"
                className="link-standalone"
                onClick={verifyIntendedPromiseRejection(async () => {
                  await proFeatureGate("filter_code")
                  updateShowAIGenerator(true)
                })}
              >
                AI code generator
              </button>
            </li>
            {generators.map(generator => (
              <li key={generator.id}>
                <button type="button" className="link-standalone" onClick={() => updateSelectedGenerator(generator.id)}>
                  {generator.title}
                </button>
              </li>
            ))}
          </ul>
        </section>
      </OptionsPanel>

      {selectedGenerator && (
        <GeneratorModal
          generator={generators.find(g => g.id === selectedGenerator)}
          onClose={() => updateSelectedGenerator(null)}
        />
      )}
      <AIGeneratorModal
        askAItoGenerateFilterCode={askAItoGenerateFilterCode}
        onClose={() => {
          updateShowAIGenerator(false)
          setPrompt("")
          setGeneratedCode("")
          setIsGenerating(false)
          setAiError("")
          setRateLimited(false)
        }}
        prompt={prompt}
        setPrompt={setPrompt}
        showAIGenerator={showAIGenerator}
        generatedCode={generatedCode}
        isGenerating={isGenerating}
        aiError={aiError}
        rateLimited={rateLimited}
        clearAiError={() => setAiError("")}
      />
    </div>
  )
}

FilterCode.propTypes = {
  contextTemplate: PropTypes.string.isRequired,
  selectedTrigger: PropTypes.object,
  selectedQueries: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedActions: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterCode: PropTypes.string,
  updateFilterCode: PropTypes.func.isRequired,
  proFeatureGate: PropTypes.func,
  // Header element the CTA portals into; nothing renders until the host mounts it.
  ctaSlot: PropTypes.object,
  userPermissions: PropTypes.shape({
    applet_data_store: PropTypes.shape({
      permitted: PropTypes.bool,
    }),
  }),
}

FilterCode.defaultProps = {
  filterCode: "",
  proFeatureGate: () => Promise.resolve(),
}
